'use client';

import { c as _c } from "react/compiler-runtime";
import { BoxIcon, BrickWallIcon, CameraIcon, ChevronDownIcon, ChevronRightIcon, FileBoxIcon, FileImageIcon, FolderIcon, LightbulbIcon, PlayIcon } from 'lucide-react';
import type { IGltfTreeNode } from './types';
import type { ItemInstance } from '@headless-tree/core';
import { cn } from '@/lib/utils';
interface GltfTreeItemProps {
  item: ItemInstance<IGltfTreeNode>;
  level: number;
  isSelected: boolean;
  isExpanded: boolean;
  isFocused: boolean;
  isFolder: boolean;
  renamingValue?: string;
}

/** Icon component for different node types */
const NodeIcon = t0 => {
  const $ = _c(20);
  const {
    type,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("size-4 shrink-0 text-muted-foreground", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const iconClass = t1;
  switch (type) {
    case "root":
      {
        let t2;
        if ($[2] !== iconClass) {
          t2 = <FileBoxIcon className={iconClass} />;
          $[2] = iconClass;
          $[3] = t2;
        } else {
          t2 = $[3];
        }
        return t2;
      }
    case "scene":
    case "object3d":
      {
        let t2;
        if ($[4] !== iconClass) {
          t2 = <FolderIcon className={iconClass} />;
          $[4] = iconClass;
          $[5] = t2;
        } else {
          t2 = $[5];
        }
        return t2;
      }
    case "mesh":
      {
        let t2;
        if ($[6] !== iconClass) {
          t2 = <BoxIcon className={iconClass} />;
          $[6] = iconClass;
          $[7] = t2;
        } else {
          t2 = $[7];
        }
        return t2;
      }
    case "material":
      {
        let t2;
        if ($[8] !== iconClass) {
          t2 = <BrickWallIcon className={iconClass} />;
          $[8] = iconClass;
          $[9] = t2;
        } else {
          t2 = $[9];
        }
        return t2;
      }
    case "texture":
      {
        let t2;
        if ($[10] !== iconClass) {
          t2 = <FileImageIcon className={iconClass} />;
          $[10] = iconClass;
          $[11] = t2;
        } else {
          t2 = $[11];
        }
        return t2;
      }
    case "animation":
      {
        let t2;
        if ($[12] !== iconClass) {
          t2 = <PlayIcon className={iconClass} />;
          $[12] = iconClass;
          $[13] = t2;
        } else {
          t2 = $[13];
        }
        return t2;
      }
    case "camera":
      {
        let t2;
        if ($[14] !== iconClass) {
          t2 = <CameraIcon className={iconClass} />;
          $[14] = iconClass;
          $[15] = t2;
        } else {
          t2 = $[15];
        }
        return t2;
      }
    case "light":
      {
        let t2;
        if ($[16] !== iconClass) {
          t2 = <LightbulbIcon className={iconClass} />;
          $[16] = iconClass;
          $[17] = t2;
        } else {
          t2 = $[17];
        }
        return t2;
      }
    default:
      {
        let t2;
        if ($[18] !== iconClass) {
          t2 = <FolderIcon className={iconClass} />;
          $[18] = iconClass;
          $[19] = t2;
        } else {
          t2 = $[19];
        }
        return t2;
      }
  }
};

/** GLTF tree item component */
export const GltfTreeItem = t0 => {
  const $ = _c(35);
  const {
    item,
    level,
    isSelected,
    isExpanded,
    isFocused,
    renamingValue
  } = t0;
  let t1;
  if ($[0] !== item) {
    t1 = item.getItemData();
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const itemData = t1;
  const hasChildren = itemData?.children && itemData.children.length > 0;
  const iconColorClass = isSelected ? "text-primary-foreground" : "text-muted-foreground";
  let t2;
  if ($[2] !== item) {
    t2 = item.getProps();
    $[2] = item;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = isSelected ? "bg-primary text-primary-foreground font-medium" : isFocused ? "bg-hover" : "hover:bg-hover";
  const t4 = isFocused && "ring-1 ring-primary/40";
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = cn("group relative m-0.5 flex h-6.5 cursor-pointer items-center gap-1 rounded-sm px-2 text-2xs transition-colors", t3, t4);
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = `${level * 14}px`;
  let t7;
  if ($[7] !== t6) {
    t7 = {
      paddingLeft: t6
    };
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== hasChildren || $[10] !== isExpanded || $[11] !== isSelected || $[12] !== item) {
    t8 = hasChildren && <button onClick={e => {
      e.stopPropagation();
      if (isExpanded) {
        item.collapse();
      } else {
        item.expand();
      }
    }} className={cn("flex size-3 items-center justify-center rounded-sm transition-colors", isSelected ? "hover:bg-primary-foreground/20" : "hover:bg-foreground/10")}>{isExpanded ? <ChevronDownIcon className={cn("size-3", isSelected && "text-primary-foreground")} /> : <ChevronRightIcon className={cn("size-3", isSelected && "text-primary-foreground")} />}</button>;
    $[9] = hasChildren;
    $[10] = isExpanded;
    $[11] = isSelected;
    $[12] = item;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <div className="flex size-4 shrink-0 items-center justify-center">{t8}</div>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = itemData?.type || "object3d";
  let t11;
  if ($[16] !== iconColorClass || $[17] !== t10) {
    t11 = <NodeIcon type={t10} className={iconColorClass} />;
    $[16] = iconColorClass;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = isSelected ? "text-primary-foreground" : "text-foreground";
  let t13;
  if ($[19] !== t12) {
    t13 = cn("flex-1 truncate", t12);
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = renamingValue !== undefined ? renamingValue : itemData?.name || "Unknown";
  let t15;
  if ($[21] !== t13 || $[22] !== t14) {
    t15 = <span className={t13}>{t14}</span>;
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== isSelected || $[25] !== itemData) {
    t16 = itemData?.type === "mesh" && itemData.data && <span className={cn("text-2xs tabular-nums", isSelected ? "text-primary-foreground/80" : "text-muted-foreground")}>{(itemData.data as any).geometry?.attributes?.position?.count || 0} vertices</span>;
    $[24] = isSelected;
    $[25] = itemData;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== t11 || $[28] !== t15 || $[29] !== t16 || $[30] !== t2 || $[31] !== t5 || $[32] !== t7 || $[33] !== t9) {
    t17 = <div {...t2} className={t5} style={t7}>{t9}{t11}{t15}{t16}</div>;
    $[27] = t11;
    $[28] = t15;
    $[29] = t16;
    $[30] = t2;
    $[31] = t5;
    $[32] = t7;
    $[33] = t9;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  return t17;
};
GltfTreeItem.displayName = 'GltfTreeItem';